import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { apiGet, apiPut } from '../../services/genesisApi/httpsMethods';
import { CustomButton } from '../../components/common/customButton';
import { areRecordsEqual, getChangedRecords } from '../../utils/compareData';
import { CustomTextInput } from '../../components/common/inputFields/customTextInput';
import type { Tenant } from '../../types/deal';
import {
  createAreaTypeSelectColumn,
  createStringSelectColumn,
} from '../../utils/columnHelpers';
import { useReferenceLists } from '../../context/GlobalGetLists';
import { HyperLinkRenderer } from '../../utils/hyperlinkRenderer';
import { numberFormatter, dateFormatter } from '../../utils/numberFormatter';
import { SimpleToolbar } from '../../components/layout/Toolbar';
import { CustomIconButton } from '../../components/common/customIconButton';
import pencilIcon from '@/assets/svg/edit.svg';
import downloadIcon from '@/assets/svg/downloads-dark.svg';
import "../../assets/css/Text-General.css"
import { useCurrentUser } from '@/context/CurrentUserContext';
import '../../assets/css/AGGrid-custom.css';
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

ModuleRegistry.registerModules([AllCommunityModule]);

type TenantsProps = {
  assetId?: number | null;
  dealId?: number | null;
};

const Tenants = ({ assetId = null, dealId = null }: TenantsProps) => {
  const { canEdit } = useCurrentUser();
  const [editMode, setEditMode] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const [filter, setFilter] = useState('');
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [newTenants, setNewTenants] = useState<Tenant[]>([]);
  const [initialTenants, setInitialTenants] = useState<Tenant[]>([]);
  const { currencies } = useReferenceLists();

   useEffect(() => {
    const fetchTenants = async () => {
      try {
        if (assetId) {
          const response = await apiGet(`tenants?assetId=${assetId}`);
          if (response && response.success && response.data) {
            setNewTenants(response.data);
            setInitialTenants(structuredClone(response.data));
          }
        } else if (dealId) {
          const response = await apiGet(`tenants?dealId=${dealId}`);
          if (response && response.success && response.data) {
            setNewTenants(response.data);
            setInitialTenants(structuredClone(response.data));
          }
        } else {
          const response = await apiGet('tenants');
          if (response && response.success && response.data) {
            setNewTenants(response.data);
            setInitialTenants(structuredClone(response.data));
          }
        }
      } catch (error) {
        console.error('Failed to fetch tenants', error);
      }
    };

    fetchTenants();
  }, [assetId, dealId]);

  useEffect(() => {
    const isEqual = areRecordsEqual(initialTenants, newTenants);
    setDirtyGrid(!isEqual);
  }, [initialTenants, newTenants]);

  const onCellValueChanged = useCallback((event: any) => {
    const { newValue, oldValue, data } = event;
    if (newValue === oldValue) return;
    // Snapshot the whole row so valueSetter side-effects flow into state.
    setNewTenants((prevTenants: Tenant[]) =>
      prevTenants.map((tenant) =>
        tenant.id === data.id ? { ...data } : tenant
      )
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    // Clone so AG Grid edits don't mutate the baseline rows.
    setNewTenants(structuredClone(initialTenants)); // Reset to original
    setDirtyGrid(false);
  };

  const handleSaveButtonPress = async () => {
    const changedItems = getChangedRecords(newTenants, initialTenants);
    const results = await Promise.all(
      changedItems.map((tenant) => {
        // Strip nested objects the API doesn't accept on the tenant payload.
        const { asset, ...body } = tenant as any;
        if (asset?.id && body.asset_id == null) body.asset_id = asset.id;
        return apiPut('tenant', tenant.id, body);
      })
    );
    const failed = results.filter((r) => r && !r.success);
    if (failed.length > 0) {
      console.error('Some tenant updates failed', failed);
      alert(`Failed to update ${failed.length} tenant(s). See console for details.`);
      return;
    }
    window.location.reload();
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const filteredRows = useMemo(() => {
    return newTenants?.filter((s) =>
      s.tenant_name?.toLowerCase().includes(filter.toLowerCase())
    );
  }, [newTenants, filter]);

  const columnDefs: ColDef[] = useMemo(() => [
    {
      field: 'asset.asset_name', headerName: 'Asset', editable: false, width: 150,
      cellRenderer: (props: any) => {
        const asset = props.data?.asset;
        if (!asset) return null;
        return HyperLinkRenderer('Assets', asset.id, asset.asset_name)(props);
      }
    },
    { field: 'tenant_name', headerName: 'Tenant', editable: editMode, width: 150, cellRenderer: HyperLinkRenderer('Tenants') },
    { field: 'tenant_sector', headerName: 'Tenant Sector', editable: editMode, width: 150 },
    { field: 'contracted_rent', headerName: 'Contracted Rent', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'passing_rent', headerName: 'Passing Rent', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'rent_free_amount', headerName: 'Rent Free Amount', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'rent_free_end_date', headerName: 'Rent Free End Date', editable: editMode, width: 100, valueFormatter: dateFormatter },
    createStringSelectColumn('rent_currency', 'Currency', currencies || [], 100, editMode),
    createAreaTypeSelectColumn(editMode, 100),
    { field: 'lease_start_date', headerName: 'Lease Start', editable: editMode, width: 100, valueFormatter: dateFormatter },
    { field: 'lease_end_date', headerName: 'Lease End', editable: editMode, width: 100, valueFormatter: dateFormatter },
    { field: 'break_option_date', headerName: 'Break Date', editable: editMode, width: 100, valueFormatter: dateFormatter },
    { field: 'next_review_date', headerName: 'Review Date', editable: editMode, width: 100, valueFormatter: dateFormatter },
    { field: 'unit_type', headerName: 'Unit Type', editable: editMode, width: 100 },
    { field: 'total_area', headerName: 'Total Area', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'estimated_rental_value', headerName: 'ERV', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'tf_erv', headerName: 'TF ERV', editable: editMode, width: 100, valueFormatter: numberFormatter },

    {
      headerName: 'Rent / Area',
      field: 'rent_per_area',
      editable: false,
      width: 100,
      valueGetter: (params) => {
        const rent = params.data?.contracted_rent;
        const area = params.data?.total_area;
        if (rent && area) {
          return rent / area;
        }
        return null;
      },
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value == null || isNaN(value)) return '';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      headerName: 'ERV / Area',
      field: 'erv_per_area',
      editable: false,
      width: 100,
      valueGetter: (params) => {
        const erv = params.data?.estimated_rental_value;
        const area = params.data?.total_area;
        if (erv && area) {
          return erv / area;
        }
        return null;
      },
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value == null || isNaN(value)) return '';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    {
      headerName: 'TF ERV / Area',
      field: 'tf_erv_per_area',
      editable: false,
      width: 100,
      valueGetter: (params) => {
        const tf_erv = params.data?.tf_erv;
        const area = params.data?.total_area;
        if (tf_erv && area) {
          return tf_erv / area;
        }
        return null;
      },
      valueFormatter: ({ value }: ValueFormatterParams) => {
        if (value == null || isNaN(value)) return '';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
    },
    { field: 'count_parking', headerName: 'No. of Parking Spaces', editable: editMode, width: 100 },
    { field: 'parking_erv', headerName: 'Parking ERV', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'parking_income', headerName: 'Parking Income', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'ios_area', headerName: 'IOS Area', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'ios_erv', headerName: 'IOS ERV', editable: editMode, width: 100, valueFormatter: numberFormatter },
    { field: 'ios_income', headerName: 'IOS Income', editable: editMode, width: 100, valueFormatter: numberFormatter },
    
  ], [editMode, currencies]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
        padding: dealId || assetId ? '0 0 16px 0' : 0,
      }}
    >

      {assetId || dealId ? null : (
        <Box sx={{ flexShrink: 0 }}>
          <SimpleToolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px', minWidth: 0 }}>
              <CustomTextInput
                label="Tenant name"
                value={filter}
                onChange={(val) => typeof val === 'string' && setFilter(val)}
              />
            </Box>
            {editMode ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }} data-position="right">
                <CustomButton text="Cancel" handleButtonPress={handleCancelButtonPress} isButtonDisabled={false} solid={false} widthOverride="100px" />
                <CustomButton text="Save" handleButtonPress={requestSave} isButtonDisabled={!dirtyGrid && !isCellEditing} solid widthOverride="100px" />
              </Box>
            ) : (
              <Box
                data-position="right"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  ml: -0.5,
                  '& .MuiIconButton-root': {
                    p: 0.6,
                  },
                  '& .MuiIconButton-root .MuiSvgIcon-root': {
                    fontSize: 22,
                  },
                }}
              >
                <CustomIconButton
                  tooltip="Download"
                  icon={
                    <Box
                      component="img"
                      src={downloadIcon}
                      alt="Download tenants"
                      sx={{ width: 30, height: 30, display: 'block' }}
                    />
                  }
                  iconButtonSx={{ p: 0 }}
                  onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'tenants' })}
                />
                <CustomIconButton
                  tooltip="Edit"
                  icon={
                    <Box
                      component="img"
                      src={pencilIcon}
                      alt="Edit tenants"
                      sx={{ width: 20, height: 20, display: 'block' }}
                    />
                  }
                  disabled={!canEdit}
                  onClick={() => {
                    if (!canEdit) return;
                    setEditMode(true);
                  }}
                />
              </Box>
            )}
          </SimpleToolbar>
        </Box>
      )}

      {/* AG Grid */}
      <Box sx={{ flexGrow: 1, minHeight: 0, p: 0 }}>
        <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
          <AgGridReact
            {...gridEditProps}
            rowData={filteredRows}
            columnDefs={columnDefs}
            ref={gridRef}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit={true}
            defaultColDef={{ resizable: true }}
            rowHeight={30}
            getRowId={({ data }) => data?.id?.toString()}
          />
        </div>
      </Box>
    </Box>
  );
};

export default Tenants;
